import React, { useEffect, useState, useCallback } from 'react';
import styles from './ReportCard.module.scss';
import { Layout } from 'components';
import Notification from 'utils/Notifications';
import moment from 'moment';

// Hooks
import useGeolocation from './hooks/useGeolocation';
import useUserRoles from './hooks/useUserRoles';
import useCamera from './hooks/useCamera';
import useLoads from './hooks/useLoads';

// Components
import ScanDataContext from './components/ScanDataContext';
import ScannerOverlay from './components/ScannerOverlay';
import ScanButton from './components/ScanButton';
import OperatorDriverPanel from './components/OperatorDriverPanel';
import InLoadingTableView from './components/InLoadingTableView';
import InRouteTableView from './components/InRouteTableView';
import CompletedLoadsTableView from './components/CompletedLoadsTableView';
import HistoryModalView from './components/HistoryModalView';
import AnalyticsSection from './components/AnalyticsSection';
import StatusModals from './components/StatusModals';
import Last3Marks from './components/Last3Marks';
import DriverCabinet from './components/DriverCabinet';
import BaseRequestsOperatorPanel from './components/BaseRequestsOperatorPanel';
import BaseDriversPanel from './components/BaseDriversPanel';
import OperatorScanModal from './components/OperatorScanModal';

// Services
import { getDriverByUid, postLoadByDriverUid } from 'service/TabelService';
import { getDriverLastLoadA, putLoadMarkB, postLoadMarkBGuard } from 'service/DriverService';
import { markExit } from 'service/LoadService';

// Utils
import { checkBranchesInRange } from './utils/geo';
import { getCurrentGeoPosition } from './utils/getGeoPosition';

const ReportCard = () => {
  // ─── State ───────────────────────────────────────────────
  const [refresh, setRefresh] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [scanData, setScanData] = useState(null);
  const [showQr, setShowQr] = useState(false);

  // Scanner steps & photo
  const [scanningStep, setScanningStep] = useState('scan');
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [, setPhotoBlob] = useState(null);
  const [pendingLoadData, setPendingLoadData] = useState(null);

  // Guard
  const [driverUid, setDriverUid] = useState(null);
  const [driverData, setDriverData] = useState(null);
  const [loadingDriver, setLoadingDriver] = useState(false);
  const [errorDriver, setErrorDriver] = useState(null);
  const [, setAllowBtnLoading] = useState(false);
  const [, setAllowBtnError] = useState(null);
  const [guardSuccess, setGuardSuccess] = useState(false);

  // Operator scan state (unified for all 3 types)
  const [topBaseScannedDriver, setTopBaseScannedDriver] = useState(null);
  const [topBaseLastLoadA, setTopBaseLastLoadA] = useState(null);
  const [topBaseScanLoading, setTopBaseScanLoading] = useState(false);
  const [topBaseScanError, setTopBaseScanError] = useState(null);
  const [topBaseConfirmLoading, setTopBaseConfirmLoading] = useState(false);
  const [topBaseConfirmError, setTopBaseConfirmError] = useState(null);
  const [topBaseSuccess, setTopBaseSuccess] = useState(false);

  const [chppScannedDriver, setChppScannedDriver] = useState(null);
  const [chppLastLoadA, setChppLastLoadA] = useState(null);
  const [chppScanLoading, setChppScanLoading] = useState(false);
  const [chppScanError, setChppScanError] = useState(null);
  const [chppConfirmLoading, setChppConfirmLoading] = useState(false);
  const [chppConfirmError, setChppConfirmError] = useState(null);
  const [chppSuccess, setChppSuccess] = useState(false);

  const [privatePointScannedDriver, setPrivatePointScannedDriver] = useState(null);
  const [privatePointLastLoadA, setPrivatePointLastLoadA] = useState(null);
  const [privatePointScanLoading, setPrivatePointScanLoading] = useState(false);
  const [privatePointScanError, setPrivatePointScanError] = useState(null);
  const [privatePointConfirmLoading, setPrivatePointConfirmLoading] = useState(false);
  const [privatePointConfirmError, setPrivatePointConfirmError] = useState(null);
  const [privatePointSuccess, setPrivatePointSuccess] = useState(false);

  // Modals
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningModalData, setWarningModalData] = useState(null);

  // Новый модальный оператор-сканер
  const [showOperatorModal, setShowOperatorModal] = useState(false);

  // Раскрывающиеся секции
  const [activeTab, setActiveTab] = useState('inLoading');
  const [showInLoading, setShowInLoading] = useState(true);
  const [showInRoute, setShowInRoute] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);

  const selectTab = (key) => {
    const next = activeTab === key ? null : key;
    setActiveTab(next);
    setShowInLoading(next === 'inLoading');
    setShowInRoute(next === 'inRoute');
    setShowHistory(next === 'history');
    setShowAnalytics(next === 'analytics');
    // 'drivers' tab has no separate boolean — controlled by activeTab directly
  };

  // ─── Hooks ───────────────────────────────────────────────
  const { userGeoLocation, setUserGeoLocation } = useGeolocation();
  const { user, isGuard, isTopBaseOperator, isChppOperator, isPrivatePointOperator, isDriverChecked, operatorBranches, roleLoading, roleError } = useUserRoles();

  // Для операторов баз — по умолчанию показывать вкладку "В пути"
  useEffect(() => {
    if (!roleLoading && (isTopBaseOperator || isChppOperator || isPrivatePointOperator)) {
      setActiveTab('inRoute');
      setShowInLoading(false);
      setShowInRoute(true);
    }
  }, [roleLoading, isTopBaseOperator, isChppOperator, isPrivatePointOperator]);

  const loads = useLoads({
    isGuard,
    isTopBaseOperator,
    isChppOperator,
    isPrivatePointOperator,
    user,
    refresh,
    onNotify: setNotify,
    showInLoading,
    showInRoute,
    showHistory,
    showAnalytics,
  });

  // ─── Helpers ─────────────────────────────────────────────
  /** Сбросить все operator scan states */
  const resetAllOperatorState = useCallback(() => {
    setTopBaseScannedDriver(null);
    setTopBaseLastLoadA(null);
    setChppScannedDriver(null);
    setChppLastLoadA(null);
    setPrivatePointScannedDriver(null);
    setPrivatePointLastLoadA(null);
    setCapturedPhoto(null);
    setPhotoBlob(null);
    setPendingLoadData(null);
  }, []);

  /** Показать модалку успеха с задержкой */
  const showSuccess = useCallback((driverDataObj, actionType = 'entry') => {
    const modalData = {
      fio: driverDataObj?.employee_name || '-',
      phone: driverDataObj?.employee_phone || '-',
      vehicleNumber: driverDataObj?.vehicle_number || '-',
      base: driverDataObj?.top_base_name || driverDataObj?.chpp_name || driverDataObj?.private_point_name || '-',
      actionType,
    };
    setTimeout(() => {
      setSuccessModalData(modalData);
      setShowSuccessModal(true);
    }, 300);
  }, []);

  /** Получить гео (из state или свежую) */
  const ensureGeo = useCallback(async () => {
    if (userGeoLocation && typeof userGeoLocation === 'string' && userGeoLocation.trim()) {
      return userGeoLocation;
    }
    const geo = await getCurrentGeoPosition();
    setUserGeoLocation(geo);
    return geo;
  }, [userGeoLocation, setUserGeoLocation]);

  /** Фоновая повторная отправка при 504 */
  const backgroundRetry = useCallback((requestFn, label) => {
    (async () => {
      for (let attempt = 1; attempt <= 3; attempt++) {
        await new Promise(r => setTimeout(r, 2500));
        try {
          console.log(`Фоновая отправка ${label}, попытка ${attempt}`);
          await requestFn();
          console.log(`Фоновая отправка ${label} успешно`);
          break;
        } catch (err) {
          console.warn(`Фоновая попытка ${attempt} не удалась`, err?.response || err.message || err);
          if (attempt === 3) console.error(`Фоновые попытки ${label} исчерпаны`);
        }
      }
    })();
  }, []);

  const is504 = useCallback((e) => {
    const status = e?.response?.status || e?.status;
    return status === 504 || (e?.message && (e.message.includes('504') || e.message.toLowerCase().includes('gateway')));
  }, []);

  // ─── Close scanner ──────────────────────────────────────
  const handleCloseScanner = useCallback(() => {
    setShowQr(false);
    setScanningStep('scan');
    resetAllOperatorState();
  }, [resetAllOperatorState]);

  // ─── QR Scanned handler ─────────────────────────────────
  const handleQRCodeScanned = useCallback(async (qrData) => {
    // --- Guard ---
    if (isGuard) {
      setShowQr(false);
      setDriverUid(qrData);
      setLoadingDriver(true);
      setErrorDriver(null);
      try {
        const res = await getDriverByUid(qrData);
        const driverStatus = res.data?.status;
        const isDriverFree = !driverStatus || driverStatus === 'Свободен';
        const lastLoadA = res.data?.last_load_a;
        const lastCompletedLoad = res.data?.last_load_b;
        const hasUncompletedTrip = lastLoadA && (!lastCompletedLoad || lastLoadA.id > lastCompletedLoad.id) && !isDriverFree;

        // Надёжный парсинг времени (формат "DD.MM.YYYY HH:mm" или ISO)
        const parseLoadTime = (timeStr) => {
          if (!timeStr) return null;
          let m = moment(timeStr, 'DD.MM.YYYY HH:mm', true);
          if (m.isValid()) return m;
          m = moment(timeStr, moment.ISO_8601, true);
          if (m.isValid()) return m;
          m = moment(timeStr); // нестрогий режим как fallback
          if (m.isValid()) return m;
          return null;
        };

        if (hasUncompletedTrip) {
          const baseName = res.data?.top_base_name || res.data?.chpp_name || res.data?.private_point_name || '-';

          // Проверяем статус по обоим полям: driver.status и load.status
          const loadStatus = lastLoadA?.status;
          const isInLoading = driverStatus === 'В погрузке' || loadStatus === 'В погрузке';
          const isInRoute   = driverStatus === 'В пути'    || loadStatus === 'В пути';

          if (isInLoading) {
            // Водитель ещё в базе — проверяем 40 мин с въезда (time_a)
            const entryTime = parseLoadTime(lastLoadA.time_a || lastLoadA.note_a_datetime || lastLoadA.created_at);
            const minutesAgo = entryTime ? moment().diff(entryTime, 'minutes') : null;

            if (minutesAgo === null) {
              // Не удалось распарсить время — всё равно помечаем выезд
              try {
                const geoToUse = await ensureGeo();
                await markExit(lastLoadA.id, { geo_a_exit: geoToUse });
                setNotify({ isOpen: true, message: 'Выезд отмечен', type: 'success' });
                setRefresh(r => !r);
                setGuardSuccess(true);
                showSuccess(res.data, 'guard_exit');
              } catch (exitErr) {
                setErrorModalData({ reason: 'Ошибка при отметке выезда', fio: res.data?.employee_name || '-', phone: res.data?.employee_phone || '-', vehicleNumber: res.data?.vehicle_number || '-', base: baseName });
                setShowErrorModal(true);
              }
              return;
            }

            if (minutesAgo >= 40) {
              // ≥ 40 мин — отмечаем выезд
              try {
                const geoToUse = await ensureGeo();
                await markExit(lastLoadA.id, { geo_a_exit: geoToUse });
                setNotify({ isOpen: true, message: 'Выезд отмечен', type: 'success' });
                setRefresh(r => !r);
                setGuardSuccess(true);
                showSuccess(res.data, 'guard_exit');
              } catch (exitErr) {
                setErrorModalData({ reason: 'Ошибка при отметке выезда', fio: res.data?.employee_name || '-', phone: res.data?.employee_phone || '-', vehicleNumber: res.data?.vehicle_number || '-', base: baseName });
                setShowErrorModal(true);
              }
            } else {
              // < 40 мин — сообщаем, сколько осталось
              setLoadingDriver(false);
              setWarningModalData({
                title: 'Водитель уже отмечен при въезде',
                message: `Водитель въехал в базу ${minutesAgo} мин. назад. Выезд можно отметить через ${40 - minutesAgo} мин.`,
                distance: baseName,
                requirement: 'Минимальное время нахождения в базе — 40 минут',
              });
              setShowWarningModal(true);
            }
            return;
          }

          if (isInRoute) {
            // Водитель уже выехал — проверяем 40 мин с выезда (time_a_exit)
            const exitMoment = parseLoadTime(lastLoadA.time_a_exit);
            const minutesSinceExit = exitMoment ? moment().diff(exitMoment, 'minutes') : null;
            setLoadingDriver(false);
            if (minutesSinceExit !== null && minutesSinceExit < 40) {
              setWarningModalData({
                title: 'Водитель уже в пути',
                message: `Водитель выехал из базы ${minutesSinceExit} мин. назад и ещё в пути.`,
                distance: baseName,
                requirement: 'Дождитесь завершения текущей доставки',
              });
              setShowWarningModal(true);
            } else {
              setErrorModalData({
                reason: 'ВОДИТЕЛЬ НЕ ЗАВЕРШИЛ ПРЕЖНЮЮ ПОГРУЗКУ',
                fio: res.data?.employee_name || '-',
                phone: res.data?.employee_phone || '-',
                vehicleNumber: res.data?.vehicle_number || '-',
                base: baseName,
              });
              setShowErrorModal(true);
            }
            return;
          }
        }
        setDriverData(res.data);
      } catch (e) {
        setErrorDriver('Ошибка загрузки данных водителя');
        setDriverData(null);
      } finally {
        setLoadingDriver(false);
      }
      return;
    }

    // --- Operator scan helper ---
    const handleOperatorScan = async (setLoading, setError, setDriver, setLastLoad, baseNameKey, noLoadMsg) => {
      setLoading(true);
      setError(null);
      try {
        const res = await getDriverLastLoadA(qrData);
        setDriver(res.data || null);
        setLastLoad(res.data?.last_load_a || null);

        const lastLoadA = res.data?.last_load_a;
        const lastLoadB = res.data?.last_load_b;
        const baseName = res.data?.[baseNameKey] || '-';

        if (lastLoadA) {
          setPendingLoadData({ loadId: lastLoadA.id, driverData: res.data });
          setScanningStep('photo');
          return;
        }

        // Нет last_load_a
        const closeAndReset = () => { setShowQr(false); setScanningStep('scan'); };

        if (lastLoadB?.time_b) {
          const minutesSince = moment().diff(moment(lastLoadB.time_b, 'DD.MM.YYYY HH:mm'), 'minutes');
          if (minutesSince >= 60) {
            closeAndReset();
            setErrorModalData({ reason: noLoadMsg, fio: res.data?.employee_name || '-', phone: res.data?.employee_phone || '-', vehicleNumber: res.data?.vehicle_number || '-', base: baseName });
            setShowErrorModal(true);
          } else {
            closeAndReset();
            setWarningModalData({ title: 'Внимание', message: `Водитель уже был отмечен`, distance: baseName, requirement: 'Нет активной погрузки для этого водителя' });
            setShowWarningModal(true);
          }
          return;
        }

        if (lastLoadB && !lastLoadB.time_b) {
          closeAndReset();
          setWarningModalData({ title: 'Внимание', message: 'Водитель уже был отмечен', distance: baseName, requirement: 'Нет активной погрузки для этого водителя' });
          setShowWarningModal(true);
          return;
        }

        // Нет ни A ни B
        closeAndReset();
        setErrorModalData({ reason: noLoadMsg, fio: res.data?.employee_name || '-', phone: res.data?.employee_phone || '-', vehicleNumber: res.data?.vehicle_number || '-', base: baseName });
        setShowErrorModal(true);
      } catch (e) {
        setError('Ошибка загрузки данных водителя или загрузки');
        setDriver(null);
        setLastLoad(null);
        setShowQr(false);
        setScanningStep('scan');
      } finally {
        setLoading(false);
      }
    };

    if (isTopBaseOperator) {
      await handleOperatorScan(setTopBaseScanLoading, setTopBaseScanError, setTopBaseScannedDriver, setTopBaseLastLoadA, 'top_base_name', 'Нет погрузки для вашей Базы');
    } else if (isChppOperator) {
      await handleOperatorScan(setChppScanLoading, setChppScanError, setChppScannedDriver, setChppLastLoadA, 'chpp_name', 'Нет погрузки для вашей ТЭЦ');
    } else if (isPrivatePointOperator) {
      await handleOperatorScan(setPrivatePointScanLoading, setPrivatePointScanError, setPrivatePointScannedDriver, setPrivatePointLastLoadA, 'private_point_name', 'Нет погрузки для вашего соц.объекта или МСУ');
    }
  }, [isGuard, isTopBaseOperator, isChppOperator, isPrivatePointOperator, ensureGeo, showSuccess]);

  // ─── Camera hook ─────────────────────────────────────────
  const camera = useCamera({
    showQr,
    onNotify: setNotify,
    onQRCodeScanned: handleQRCodeScanned,
  });

  // ─── capturePhoto (автоматически отправляет) ────────────
  const capturePhoto = useCallback(async () => {
    if (!camera.videoRef.current || !camera.canvasRef.current || !pendingLoadData) return;

    const video = camera.videoRef.current;
    const canvas = camera.canvasRef.current;
    const context = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedPhoto(dataUrl);

    const blob = await new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/jpeg', 0.8));
    if (!blob) {
      setNotify({ isOpen: true, message: 'Не удалось получить фото с камеры', type: 'error' });
      return;
    }
    setPhotoBlob(blob);
    setScanningStep('sending');

    const capturedLoadId = pendingLoadData.loadId;
    const capturedDriverData = pendingLoadData.driverData;
    let formData = null;

    const onSubmitSuccess = () => {
      setNotify({ isOpen: true, message: 'Готово!', type: 'success' });
      setRefresh(r => !r);
      setShowQr(false);
      setScanningStep('scan');
      resetAllOperatorState();
      if (isTopBaseOperator) setTopBaseSuccess(true);
      else if (isChppOperator) setChppSuccess(true);
      else if (isPrivatePointOperator) setPrivatePointSuccess(true);
      showSuccess(capturedDriverData, 'exit');
    };

    try {
      const geoToUse = await ensureGeo();
      formData = new FormData();
      formData.append('time_b', new Date().toISOString());
      formData.append('geo_b', geoToUse);
      formData.append('photo_b', blob, 'transport.jpg');

      await putLoadMarkB(capturedLoadId, formData);
      onSubmitSuccess();
    } catch (e) {
      if (is504(e) && capturedLoadId && formData) {
        onSubmitSuccess();
        backgroundRetry(() => putLoadMarkB(capturedLoadId, formData), 'фото');
        return;
      }
      // fall through to setNotify below
      setNotify({ isOpen: true, message: 'Ошибка отправки: ' + (e.message || 'Неизвестная ошибка'), type: 'error' });
      setScanningStep('photo');
    }
  }, [camera.videoRef, camera.canvasRef, pendingLoadData, isTopBaseOperator, isChppOperator, isPrivatePointOperator, ensureGeo, showSuccess, resetAllOperatorState, is504, backgroundRetry]);

  const retakePhoto = useCallback(() => {
    setCapturedPhoto(null);
    setPhotoBlob(null);
    setScanningStep('photo');
  }, []);

  // ─── Guard: handleAllow ─────────────────────────────────
  const handleAllow = useCallback(async (autoSubmit = false) => {
    if (!driverUid) return;

    let geoToUse;
    try {
      geoToUse = await ensureGeo();
    } catch (err) {
      setAllowBtnError('Геолокация обязательна для действия! Включите геолокацию и попробуйте снова.');
      return;
    }

    setAllowBtnLoading(true);
    setAllowBtnError(null);
    setGuardSuccess(false);
    try {
      await postLoadByDriverUid(driverUid, { time_a: new Date().toISOString(), geo_a: geoToUse });
      setNotify({ isOpen: true, message: 'Готово', type: 'success' });
      setRefresh(r => !r);
      setGuardSuccess(true);

      if (autoSubmit) {
        const currentDriverData = driverData;
        setDriverData(null);
        showSuccess(currentDriverData, 'entry');
      } else {
        setDriverData(null);
      }
    } catch (e) {
      setAllowBtnError('Ошибка отправки');
    } finally {
      setAllowBtnLoading(false);
    }
  }, [driverUid, ensureGeo, driverData, showSuccess]);

  // ─── Operator confirmations (unified) ───────────────────
  const handleOperatorConfirm = useCallback(async (lastLoadA, setLoading, setError, setSuccess, setDriver, setLastLoadState, autoSubmit = false) => {
    if (!lastLoadA?.id) return;

    let geoToUse;
    try {
      geoToUse = await ensureGeo();
    } catch (err) {
      setError('Геолокация обязательна для действия! Включите геолокацию и попробуйте снова.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await postLoadMarkBGuard(lastLoadA.id, { time_b: new Date().toISOString(), geo_b: geoToUse });
      setNotify({ isOpen: true, message: 'Получение подтверждено', type: 'success' });
      setSuccess(true);
      setRefresh(r => !r);
      setDriver(null);
      setLastLoadState(null);
    } catch (e) {
      setError('Ошибка подтверждения');
    } finally {
      setLoading(false);
    }
  }, [ensureGeo]);

  const handleConfirmTopBase = useCallback((auto) =>
    handleOperatorConfirm(topBaseLastLoadA, setTopBaseConfirmLoading, setTopBaseConfirmError, setTopBaseSuccess, setTopBaseScannedDriver, setTopBaseLastLoadA, auto),
    [topBaseLastLoadA, handleOperatorConfirm]
  );
  const handleConfirmChpp = useCallback((auto) =>
    handleOperatorConfirm(chppLastLoadA, setChppConfirmLoading, setChppConfirmError, setChppSuccess, setChppScannedDriver, setChppLastLoadA, auto),
    [chppLastLoadA, handleOperatorConfirm]
  );
  const handleConfirmPrivatePoint = useCallback((auto) =>
    handleOperatorConfirm(privatePointLastLoadA, setPrivatePointConfirmLoading, setPrivatePointConfirmError, setPrivatePointSuccess, setPrivatePointScannedDriver, setPrivatePointLastLoadA, auto),
    [privatePointLastLoadA, handleOperatorConfirm]
  );

  // ─── Operator modal: QR scanned handler ────────────────
  const handleOperatorModalQrScanned = useCallback(async (uid) => {
    try {
      const res = await getDriverLastLoadA(uid);
      const data = res.data;
      const lastLoadA = data?.last_load_a;
      const lastLoadB = data?.last_load_b;
      const baseName = data?.top_base_name || data?.chpp_name || data?.private_point_name || '-';

      if (lastLoadA) {
        return { driverData: data, lastLoadA };
      }

      // Нет активной погрузки
      if (lastLoadB?.time_b) {
        const minutesSince = moment().diff(moment(lastLoadB.time_b, 'DD.MM.YYYY HH:mm'), 'minutes');
        if (minutesSince >= 60) {
          return { error: `Нет активной погрузки для водителя (база: ${baseName})` };
        }
        return { warning: `Водитель уже был отмечен (база: ${baseName}). Нет активной погрузки.` };
      }
      if (lastLoadB && !lastLoadB.time_b) {
        return { warning: `Водитель уже был отмечен (база: ${baseName}). Нет активной погрузки.` };
      }
      return { error: `Нет активной погрузки для данного водителя (база: ${baseName})` };
    } catch (e) {
      throw new Error('Ошибка загрузки данных водителя');
    }
  }, []);

  // ─── Operator modal: confirm (photo + geo → backend) ───
  const handleOperatorModalConfirm = useCallback(async ({ loadId, driverData: drvData, photoBlob, geo }) => {
    const formData = new FormData();
    formData.append('time_b', new Date().toISOString());
    formData.append('geo_b', geo || '');
    formData.append('photo_b', photoBlob, 'transport.jpg');

    const doSend = () => putLoadMarkB(loadId, formData);

    try {
      await doSend();
    } catch (e) {
      if (is504(e)) {
        backgroundRetry(doSend, 'operator-photo');
        // Считаем успехом (504 = данные дошли, сервер долго отвечал)
      } else {
        throw e;
      }
    }

    setNotify({ isOpen: true, message: 'Готово!', type: 'success' });
    setRefresh(r => !r);
    if (isTopBaseOperator) setTopBaseSuccess(true);
    else if (isChppOperator) setChppSuccess(true);
    else if (isPrivatePointOperator) setPrivatePointSuccess(true);
    showSuccess(drvData, 'exit');
    setShowOperatorModal(false);
  }, [is504, backgroundRetry, isTopBaseOperator, isChppOperator, isPrivatePointOperator, showSuccess]);

  // ─── Auto-submit for guard ──────────────────────────────
  useEffect(() => {
    if (isGuard && driverData && !loadingDriver && !errorDriver) {
      if (driverData.status === 'Заблокирован' || driverData.status === 'Не активен' || driverData.status === 'В пути') {
        setErrorModalData({
          fio: driverData.employee_name || '-',
          phone: driverData.employee_phone || '-',
          vehicleNumber: driverData.vehicle_number || '-',
          base: driverData.top_base_name || '-',
          reason: driverData.status === 'Заблокирован' ? 'ВОДИТЕЛЬ ЗАБЛОКИРОВАН' :
                  driverData.status === 'Не активен' ? 'ВОДИТЕЛЬ НЕ АКТИВЕН' :
                  'ВОДИТЕЛЬ В ПУТИ (НЕ ДОСТАВИЛ ПРЕЖНЮЮ ПОГРУЗКУ)',
        });
        setShowErrorModal(true);
      } else {
        handleAllow(true);
      }
    }
  }, [driverData, loadingDriver, errorDriver, isGuard, handleAllow]);

  // ─── Auto-submit for operators (in range) ───────────────
  useEffect(() => {
    if (isTopBaseOperator && topBaseScannedDriver && topBaseLastLoadA && !topBaseScanLoading && !topBaseScanError) {
      const { isInRange } = checkBranchesInRange(userGeoLocation, operatorBranches);
      if (isInRange) handleConfirmTopBase(true);
    }
  }, [topBaseScannedDriver, topBaseLastLoadA, topBaseScanLoading, topBaseScanError, isTopBaseOperator, userGeoLocation, operatorBranches, handleConfirmTopBase]);

  useEffect(() => {
    if (isChppOperator && chppScannedDriver && chppLastLoadA && !chppScanLoading && !chppScanError && chppScannedDriver.status !== 'Не активен') {
      const { isInRange } = checkBranchesInRange(userGeoLocation, operatorBranches);
      if (isInRange) handleConfirmChpp(true);
    }
  }, [chppScannedDriver, chppLastLoadA, chppScanLoading, chppScanError, isChppOperator, userGeoLocation, operatorBranches, handleConfirmChpp]);

  useEffect(() => {
    if (isPrivatePointOperator && privatePointScannedDriver && privatePointLastLoadA && !privatePointScanLoading && !privatePointScanError && privatePointScannedDriver.status !== 'Не активен') {
      const { isInRange } = checkBranchesInRange(userGeoLocation, operatorBranches);
      if (isInRange) handleConfirmPrivatePoint(true);
    }
  }, [privatePointScannedDriver, privatePointLastLoadA, privatePointScanLoading, privatePointScanError, isPrivatePointOperator, userGeoLocation, operatorBranches, handleConfirmPrivatePoint]);

  // ─── Out-of-range warning for operators ──────────────────
  useEffect(() => {
    const checkAndWarn = (scannedDriver, lastLoad, setDriver, setLastLoadState, requirementText) => {
      const { isInRange, distanceInfo } = checkBranchesInRange(userGeoLocation, operatorBranches);
      if (!isInRange && distanceInfo && distanceInfo !== 'Геолокация недоступна. Разрешите доступ к геолокации.') {
        setWarningModalData({
          title: 'ОПЕРАТОР ВНЕ ЗОНЫ ДЕЙСТВИЯ ОТМЕТКИ',
          message: 'Вы находитесь вне зоны допустимой для подтверждения получения.',
          distance: distanceInfo,
          requirement: requirementText,
        });
        setShowWarningModal(true);
        setShowQr(false);
        setScanningStep('scan');
        setCapturedPhoto(null);
        setPhotoBlob(null);
        setPendingLoadData(null);
        setDriver(null);
        setLastLoadState(null);
      }
    };

    if (isTopBaseOperator && topBaseScannedDriver && topBaseLastLoadA && !topBaseScanLoading && !topBaseScanError) {
      checkAndWarn(topBaseScannedDriver, topBaseLastLoadA, setTopBaseScannedDriver, setTopBaseLastLoadA, 'Необходимо находиться в радиусе 200 метров от одного из филиалов базы.');
    }
    if (isChppOperator && chppScannedDriver && chppLastLoadA && !chppScanLoading && !chppScanError && chppScannedDriver.status !== 'Не активен') {
      checkAndWarn(chppScannedDriver, chppLastLoadA, setChppScannedDriver, setChppLastLoadA, 'Необходимо находиться в радиусе 200 метров от одного из филиалов ТЭЦ.');
    }
    if (isPrivatePointOperator && privatePointScannedDriver && privatePointLastLoadA && !privatePointScanLoading && !privatePointScanError && privatePointScannedDriver.status !== 'Не активен') {
      checkAndWarn(privatePointScannedDriver, privatePointLastLoadA, setPrivatePointScannedDriver, setPrivatePointLastLoadA, 'Необходимо находиться в радиусе 200 метров от одного из филиалов соц.объекта или МСУ.');
    }
  }, [
    topBaseScannedDriver, topBaseLastLoadA, topBaseScanLoading, topBaseScanError, isTopBaseOperator,
    chppScannedDriver, chppLastLoadA, chppScanLoading, chppScanError, isChppOperator,
    privatePointScannedDriver, privatePointLastLoadA, privatePointScanLoading, privatePointScanError, isPrivatePointOperator,
    userGeoLocation, operatorBranches,
  ]);

  // ─── Open scanner helper ────────────────────────────────
  const openScanner = useCallback((resetSuccessFn) => {
    resetSuccessFn(false);
    setShowQr(true);
  }, []);

  // ─── Render ─────────────────────────────────────────────
  return (
    <Layout>
      <div>
        {/* Role check loading — nothing renders until we know who the user is */}
        {roleLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: 16, color: '#6b7280' }}>
            <div style={{ width: 40, height: 40, border: '4px solid #e5e7eb', borderTop: '4px solid #374151', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: 14 }}>Загрузка...</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Driver Cabinet - shown for self-registered drivers */}
        {!roleLoading && isDriverChecked && !isGuard && !isTopBaseOperator && !isChppOperator && !isPrivatePointOperator && (
          <DriverCabinet />
        )}

        {!roleLoading && !isDriverChecked && (
        <div className="qr-container">
          <div>
            {/* Header */}
            <div className={styles.legend}>
              <h2>Центр управления и контроля логистики ОАО "Кыргызкомур"</h2>
              <button onClick={() => window.location.reload()} className={styles.refreshButton}>ОБНОВИТЬ</button>
            </div>
            {/* <div className={styles.refreshHint}>При возникновении сбоев попробуйте обновить страницу</div> */}

            {/* Legend
            <div className={styles.legend}>
              <div className={styles.legendItem}>
                <div aria-hidden className={`${styles.legendBox} ${styles.legendBoxDanger}`} />
                <div className={styles.legendText}>Отметка была совершена вне зоны диапазона действия</div>
              </div>
              <div className={styles.legendItem}>
                <div aria-hidden className={`${styles.legendBox} ${styles.legendBoxDuration}`} />
                <div className={styles.legendText}>Длительность перевозки превышает норму</div>
              </div>
            </div> */}

            {/* Scan buttons */}
            {(isGuard || isTopBaseOperator || isChppOperator || isPrivatePointOperator) && (
            <div style={{ padding: '0px 0', marginBottom: '0px' }}>
              {roleError && <div style={{ color: 'red' }}>{roleError}</div>}
              <ScanDataContext.Provider value={{ scanData, setScanData }}>
                <div className="combined-app">
                  {isGuard && (
                    <ScanButton
                      label="Сканировать QR"
                      success={guardSuccess}
                      onScan={() => openScanner(setGuardSuccess)}
                      onNotify={setNotify}
                    />
                  )}
                  {isTopBaseOperator && (
                    <ScanButton
                      label="Сканировать"
                      success={topBaseSuccess}
                      onScan={() => { setTopBaseSuccess(false); setShowOperatorModal(true); }}
                      onNotify={setNotify}
                    />
                  )}
                  {isChppOperator && (
                    <ScanButton
                      label="Сканировать"
                      success={chppSuccess}
                      onScan={() => { setChppSuccess(false); setShowOperatorModal(true); }}
                      onNotify={setNotify}
                    />
                  )}
                  {isPrivatePointOperator && (
                    <ScanButton
                      label="Сканировать"
                      success={privatePointSuccess}
                      onScan={() => { setPrivatePointSuccess(false); setShowOperatorModal(true); }}
                      onNotify={setNotify}
                    />
                  )}
                </div>
              </ScanDataContext.Provider>
            </div>
            )}

            {/* Last 3 Marks */}
            {/* <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '16px', marginTop: '24px', color: '#2c3e50' }}>Последние отметки (В погрузке)</h2>
            <Last3Marks loads={loads.last3Marks} loading={loads.last3Loading} /> */}

            {/* Base requests panel for operators */}
            {(isTopBaseOperator || isChppOperator || isPrivatePointOperator) && (
              <BaseRequestsOperatorPanel />
            )}

            {/* Section tab nav */}
            <div className={styles.rcNavBar}>
              {[
                { key: 'inLoading',  icon: '⬇️',  label: 'В погрузке',  count: loads.tabCounts.in_loading },
                { key: 'inRoute',    icon: '🚛',  label: 'В пути',      count: loads.tabCounts.in_route },
                { key: 'history',    icon: '📜',  label: 'История',     count: loads.tabCounts.completed },
                { key: 'analytics',  icon: '📊',  label: 'Аналитика' },
                ...((isTopBaseOperator || isChppOperator || isPrivatePointOperator)
                  ? [{ key: 'drivers', icon: '👥', label: 'Наши водители', count: loads.tabCounts.drivers }]
                  : []),
              ].map(({ key, icon, label, count }) => {
                const isActive = activeTab === key;
                return (
                  <button
                    key={key}
                    className={`${styles.rcNavTab} ${isActive ? styles.rcNavTabActive : ''}`}
                    onClick={() => selectTab(key)}
                  >
                    <span className={styles.rcNavTabIcon}>{icon}</span>
                    <span className={styles.rcNavTabLabel}>{label}</span>
                    {count > 0 && (
                      <span className={isActive ? styles.rcNavTabBadge : styles.rcNavTabBadgeInactive}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* In-loading table collapsible */}
            {showInLoading && (
              <InLoadingTableView
                  inLoadingLoads={loads.inLoadingLoads}
                  inLoadingCount={loads.inLoadingCount}
                  inLoadingNext={loads.inLoadingNext}
                  inLoadingPrev={loads.inLoadingPrev}
                  inLoadingPage={loads.inLoadingPage}
                  setInLoadingPage={loads.setInLoadingPage}
                  inLoadingSearch={loads.inLoadingSearch}
                  setInLoadingSearch={loads.setInLoadingSearch}
                  nowTime={loads.nowTime}
                  user={user}
              />
            )}

            {/* Scanner overlay */}
            {showQr && (
              <ScannerOverlay
                isGuard={isGuard}
                scanningStep={scanningStep}
                capturedPhoto={capturedPhoto}
                videoRef={camera.videoRef}
                canvasRef={camera.canvasRef}
                switchCamera={camera.switchCamera}
                selectCamera={camera.selectCamera}
                availableCameras={camera.availableCameras}
                currentCameraInfo={camera.currentCameraInfo}
                isCameraLoading={camera.isCameraLoading}
                capturePhoto={capturePhoto}
                retakePhoto={retakePhoto}
                handleSendPhoto={() => {}} // send happens in capturePhoto automatically
                handleCloseScanner={handleCloseScanner}
              />
            )}

            {/* Guard driver info */}
            {isGuard && (
              <>
                {loadingDriver && <div>Загрузка данных водителя...</div>}
                {errorDriver && <div style={{ color: 'red' }}>{errorDriver}</div>}
              </>
            )}

            {/* Operator panels */}
            {isTopBaseOperator && (
              <OperatorDriverPanel
                operatorType="topbase"
                scannedDriver={topBaseScannedDriver}
                lastLoadA={topBaseLastLoadA}
                scanLoading={topBaseScanLoading}
                scanError={topBaseScanError}
                confirmLoading={topBaseConfirmLoading}
                confirmError={topBaseConfirmError}
                userGeoLocation={userGeoLocation}
                operatorBranches={operatorBranches}
                onConfirm={handleConfirmTopBase}
              />
            )}
            {isChppOperator && (
              <OperatorDriverPanel
                operatorType="chpp"
                scannedDriver={chppScannedDriver}
                lastLoadA={chppLastLoadA}
                scanLoading={chppScanLoading}
                scanError={chppScanError}
                confirmLoading={chppConfirmLoading}
                confirmError={chppConfirmError}
                userGeoLocation={userGeoLocation}
                operatorBranches={operatorBranches}
                onConfirm={handleConfirmChpp}
              />
            )}
            {isPrivatePointOperator && (
              <OperatorDriverPanel
                operatorType="privatepoint"
                scannedDriver={privatePointScannedDriver}
                lastLoadA={privatePointLastLoadA}
                scanLoading={privatePointScanLoading}
                scanError={privatePointScanError}
                confirmLoading={privatePointConfirmLoading}
                confirmError={privatePointConfirmError}
                userGeoLocation={userGeoLocation}
                operatorBranches={operatorBranches}
                onConfirm={handleConfirmPrivatePoint}
              />
            )}

            {/* In-route table collapsible */}
            {showInRoute && (
              <InRouteTableView
                  inRouteLoads={loads.inRouteLoads}
                  inRouteCount={loads.inRouteCount}
                  inRouteNext={loads.inRouteNext}
                  inRoutePrev={loads.inRoutePrev}
                  inRoutePage={loads.inRoutePage}
                  setInRoutePage={loads.setInRoutePage}
                  inRouteSearch={loads.inRouteSearch}
                  setInRouteSearch={loads.setInRouteSearch}
                  nowTime={loads.nowTime}
                  user={user}
              />
            )}

            {/* Completed loads collapsible */}
            {showHistory && (
              <CompletedLoadsTableView
                  completedLoads={loads.completedLoads}
                  completedCount={loads.completedCount}
                  completedNext={loads.completedNext}
                  completedPrev={loads.completedPrev}
                  completedPage={loads.completedPage}
                  setCompletedPage={loads.setCompletedPage}
                  completedSearch={loads.completedSearch}
                  setCompletedSearch={loads.setCompletedSearch}                  user={user}                
                />
            )}
          </div>

          {/* Analytics collapsible */}
          <div style={{ width: '100%' }}>
            {showAnalytics && (
              <AnalyticsSection
                statsLoading={loads.statsLoading}
                statsData={loads.statsData}
                blockedDrivers={loads.blockedDrivers}
                blockedDriversLoading={loads.blockedDriversLoading}
                blockedDriversError={loads.blockedDriversError}
                blockedDriversPage={loads.blockedDriversPage}
                setBlockedDriversPage={loads.setBlockedDriversPage}
                blockedDriversCount={loads.blockedDriversCount}
                blockedPageSize={loads.BLOCKED_PAGE_SIZE}
              />
            )}
          </div>

          {/* Our drivers panel — shown via tab */}
          {activeTab === 'drivers' && (isTopBaseOperator || isChppOperator || isPrivatePointOperator) && (
            <BaseDriversPanel />
          )}
        </div>
        )}
      </div>

      <Notification notify={notify} setNotify={setNotify} />

      {/* History modal */}
      {loads.showHistoryModal && (
        <HistoryModalView
          arrivedBLoads={loads.arrivedBLoads}
          arrivedBCount={loads.arrivedBCount}
          arrivedBNext={loads.arrivedBNext}
          arrivedBPrev={loads.arrivedBPrev}
          arrivedBPage={loads.arrivedBPage}
          setArrivedBPage={loads.setArrivedBPage}
          arrivedBSearch={loads.arrivedBSearch}
          setArrivedBSearch={loads.setArrivedBSearch}
          historyLoading={loads.historyLoading}
          historyTableLoading={loads.historyTableLoading}
          loadHistory={loads.loadHistory}
          onClose={() => loads.setShowHistoryModal(false)}
          user={user}
        />
      )}

      {/* Status modals */}
      <StatusModals
        showSuccessModal={showSuccessModal}
        successModalData={successModalData}
        onCloseSuccess={() => { setShowSuccessModal(false); setSuccessModalData(null); }}
        showErrorModal={showErrorModal}
        errorModalData={errorModalData}
        onCloseError={() => { setShowErrorModal(false); setErrorModalData(null); setDriverData(null); }}
        showWarningModal={showWarningModal}
        warningModalData={warningModalData}
        onCloseWarning={() => { setShowWarningModal(false); setWarningModalData(null); }}
      />

      {/* Operator scan modal (QR + фото транспорта) */}
      {showOperatorModal && (isTopBaseOperator || isChppOperator || isPrivatePointOperator) && (
        <OperatorScanModal
          onClose={() => setShowOperatorModal(false)}
          onQRScanned={handleOperatorModalQrScanned}
          onConfirm={handleOperatorModalConfirm}
          ensureGeo={ensureGeo}
        />
      )}
    </Layout>
  );
};

export default ReportCard;
